import { buildInterviewMessage } from '../utils/interviewMessages';
import { getUiLocale, formatUiDate } from '../utils/uiTranslation';
import React, { useState, useEffect } from 'react';
import { ref, get, set, update, push } from 'firebase/database';
import { database } from '../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { emitAtsAnalyticsEvent } from '../services/atsAnalyticsService';
import { buildActorAnalyticsContext, normalizeAnalyticsSource } from '../utils/analyticsEntityFields';
import useTeamMembers from '../hooks/useTeamMembers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const InterviewScheduler = ({
  applicantId,
  userUID,
  onClose,
  onScheduleSuccess,
  jobTitle = '',
  companyName = '',
  existingInterview = null,
  substageTitle = '',
  substageData = null // New prop for interview step data (interviewers, duration)
}) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [applicantData, setApplicantData] = useState(null);
  const [interviewTitle, setInterviewTitle] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [interviewerName, setInterviewerName] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [locationType, setLocationType] = useState('virtual');
  const [address, setAddress] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState('');
  const [statusTone, setStatusTone] = useState('info');
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [suggestedTimes, setSuggestedTimes] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');

  // Time slot selection state
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // Collaborator selection state
  const { teamMembers } = useTeamMembers(userUID);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [showInvitePrompt, setShowInvitePrompt] = useState(false);
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [actualCompanyName, setActualCompanyName] = useState(companyName || '');

  useEffect(() => {
    loadApplicantData();
    loadCalendarConnections();
    loadCompanyData();

    // Initialize from substage data if available
    if (substageData) {
      if (substageData.duration) {
        setDuration(substageData.duration);
      }
      if (substageData.interviewers) {
        const interviewerIds = Object.keys(substageData.interviewers).filter(id => substageData.interviewers[id] === true);
        // IMPORTANT: Filter out the organizer (userUID) because they are automatically added by calendar
        // If we include them as attendee, they become "needsAction" instead of "accepted"
        const filteredInterviewerIds = interviewerIds.filter(id => id !== userUID);
        // Deduplicate in case the same UID appears multiple times
        const uniqueInterviewerIds = [...new Set(filteredInterviewerIds)];
        setSelectedCollaborators(uniqueInterviewerIds);
      }
      if (substageData.locationType) {
        setLocationType(substageData.locationType);
      }
      if (substageData.address) {
        setAddress(substageData.address);
      }
    }
  }, [applicantId, userUID, substageData, t, jobTitle]);

  // Load suggested times after calendar connections are loaded
  useEffect(() => {
    if (calendarConnections.length > 0) {
      loadSuggestedTimes();
    }
  }, [calendarConnections, duration]);

  const updateStatus = (content, tone = 'info') => {
    setScheduleStatus(content);
    setStatusTone(tone);
  };

  // Pre-populate form when rescheduling an existing interview
  useEffect(() => {
    if (existingInterview) {
      setInterviewTitle(existingInterview.title || '');
      setDuration(existingInterview.duration || 60);
      setInterviewerName(existingInterview.interviewer || '');
      setAdditionalNotes(existingInterview.notes || '');
      if (existingInterview.locationType) {
        setLocationType(existingInterview.locationType);
      }
      if (existingInterview.address) {
        setAddress(existingInterview.address);
      }
      // Note: We don't set the date/time - user needs to pick a new one
    }
  }, [existingInterview]);

  // Update interview title when data loads (for new interviews only)
  useEffect(() => {
    if (!existingInterview) {
      const jobLabel = jobTitle || t('interviewScheduler.position');
      const titleLabel = substageTitle || t('interview.title');
      setInterviewTitle(t('interviewScheduler.defaultTitle', { title: titleLabel, job: jobLabel }));
    }
  }, [jobTitle, substageTitle, existingInterview, t]);

  const loadApplicantData = async () => {
    const fallbackName = t('interviewScheduler.candidate');
    const fallbackJob = jobTitle || t('interviewScheduler.position');
    try {
      let applicantInfo = null;

      // First try: check connections (try both authenticated and imported patterns)
      let connectionKey = `${userUID}_${applicantId}`;
      let connectionRef = ref(database, `connections/${connectionKey}`);
      let connectionSnapshot = await get(connectionRef);

      // If not found and applicantId starts with 'temp_', try imported pattern
      if (!connectionSnapshot.exists() && applicantId.startsWith('temp_')) {
        connectionKey = `${userUID}_imported_${applicantId}`;
        connectionRef = ref(database, `connections/${connectionKey}`);
        connectionSnapshot = await get(connectionRef);
      }

      if (connectionSnapshot.exists()) {
        const connectionData = connectionSnapshot.val();
        applicantInfo = {
          name: connectionData.applicantName || connectionData.name || fallbackName,
          email: connectionData.applicantEmail || connectionData.email,
          jobTitle: connectionData.jobTitle || fallbackJob,
          candidateId: connectionData.candidateId || applicantId,
          roleId: connectionData.roleId || connectionData.jobId || '',
          ownerRecruiterId: connectionData.ownerRecruiterId || '',
          ownerRecruiterEmail: connectionData.ownerRecruiterEmail || '',
          primarySource: connectionData.primarySource || connectionData.source || '',
          sourceCategory: connectionData.sourceCategory || '',
          sourceTags: connectionData.sourceTags || [],
          utmSource: connectionData.utm_source || connectionData.utmSource || '',
          connectionKey
        };
      }

      // Second try: check people collection for authenticated users OR imported profiles for imported users
      if (!applicantInfo?.email) {
        if (applicantId.startsWith('temp_')) {
          // For imported candidates, check imported_profiles (NEW path first)
          let importedRef = ref(database, `imported_profiles/${userUID}/${applicantId}`);
          let importedSnapshot = await get(importedRef);

          // Fallback to old path
          if (!importedSnapshot.exists()) {
            importedRef = ref(database, `imported_profiles/${applicantId}`);
            importedSnapshot = await get(importedRef);
          }

          if (importedSnapshot.exists()) {
            const importedData = importedSnapshot.val();
            applicantInfo = {
              name: importedData.name || fallbackName,
              email: importedData.email,
              jobTitle: fallbackJob,
              candidateId: importedData.candidateId || applicantId,
              roleId: importedData.roleId || '',
              ownerRecruiterId: importedData.ownerRecruiterId || '',
              ownerRecruiterEmail: importedData.ownerRecruiterEmail || '',
              primarySource: importedData.primarySource || importedData.source || '',
              sourceCategory: importedData.sourceCategory || '',
              sourceTags: importedData.sourceTags || [],
              utmSource: importedData.utm_source || importedData.utmSource || ''
            };
          }
        } else {
          // For authenticated users, check people collection
          const peopleRef = ref(database, `people/${applicantId}`);
          const peopleSnapshot = await get(peopleRef);

          if (peopleSnapshot.exists()) {
            const peopleData = peopleSnapshot.val();
            applicantInfo = {
              name: peopleData.username || peopleData.name || fallbackName,
              email: peopleData.email,
              jobTitle: fallbackJob
            };
          }
        }
      }

      // Third try: get email via cloud function if still missing
      if (!applicantInfo?.email) {
        try {
          const emailResponse = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: applicantId })
          });
          const emailData = await emailResponse.json();

          if (emailData.email) {
            applicantInfo = {
              name: applicantInfo?.name || fallbackName,
              email: emailData.email,
              jobTitle: fallbackJob
            };
          }
        } catch (error) {
          console.warn('Could not fetch email via cloud function:', error);
        }
      }

      setApplicantData(applicantInfo);

      // Note: Interview title will be set by useEffect when company name loads

    } catch (error) {
      console.error('Error loading applicant data:', error);
      setApplicantData({
        name: fallbackName,
        email: 'unknown@example.com',
        jobTitle: fallbackJob
      });
    }
  };

  const loadCalendarConnections = async () => {
    try {
      setDebugInfo('Loading calendar connections...');
      const connectionsRef = ref(database, `companies/${userUID}/calendarIntegrations`);
      const snapshot = await get(connectionsRef);

      if (snapshot.exists()) {
        const connections = snapshot.val();
        const connectionsArray = Object.values(connections);
        setCalendarConnections(connectionsArray);
        setDebugInfo(`Found ${connectionsArray.length} calendar connection(s): ${connectionsArray.map(c => c.provider).join(', ')}`);
      } else {
        setCalendarConnections([]);
        setDebugInfo('No calendar connections found in Firebase');
      }
    } catch (error) {
      console.error('Error loading calendar connections:', error);
      setCalendarConnections([]);
      setDebugInfo(`Error loading calendar connections: ${error.message}`);
    }
  };

  const loadCompanyData = async () => {
    try {
      const companyRef = ref(database, `companies/${userUID}`);
      const snapshot = await get(companyRef);

      if (snapshot.exists()) {
        const companyData = snapshot.val();
        if (companyData.contactName) {
          setInterviewerName(companyData.contactName);
        }
        // Update companyName state with actual company name from Firebase
        if (companyData.companyName) {
          setActualCompanyName(companyData.companyName);
        }
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const inviteNewCollaborator = async () => {
    if (!newCollaboratorEmail || !newCollaboratorEmail.includes('@')) {
      alert(t('interviewScheduler.alerts.validEmail'));
      return;
    }

    try {
      const companyRef = ref(database, `companies/${userUID}`);
      const companySnapshot = await get(companyRef);
      const companyData = companySnapshot.val();

      const response = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/send-team-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employerUid: userUID,
          email: newCollaboratorEmail,
          companyName: companyData?.companyName || actualCompanyName
        }),
      });

      const result = await response.json();
      if (result.success) {
        alert(t('interviewScheduler.alerts.inviteSent', { email: newCollaboratorEmail }));
        setNewCollaboratorEmail('');
        setShowInvitePrompt(false);
      } else {
        alert(t('interviewScheduler.alerts.inviteFailed'));
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(t('interviewScheduler.alerts.inviteFailed'));
    }
  };

  const toggleCollaboratorSelection = (collaboratorId) => {
    setSelectedCollaborators(prev =>
      prev.includes(collaboratorId)
        ? prev.filter(id => id !== collaboratorId)
        : [...prev, collaboratorId]
    );
  };

  const loadSuggestedTimes = async () => {
    if (calendarConnections.length === 0) {
      setDebugInfo('Cannot load suggested times: No calendar connections');
      return;
    }

    setIsLoadingSuggestions(true);
    setDebugInfo('Loading suggested times from backend...');

    try {
      // Find any calendar connection (Google or Outlook)
      const calendarConnection = calendarConnections.find(conn =>
        conn.provider === 'google' || conn.provider === 'outlook'
      );

      if (!calendarConnection) {
        setDebugInfo('No calendar connection found (Google or Outlook)');
        setIsLoadingSuggestions(false);
        return;
      }

      setDebugInfo(`Found ${calendarConnection.provider} connection, making backend request...`);

      const today = new Date();
      const nextWeek = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));

      const requestPayload = {
        user_id: userUID,
        duration_minutes: duration,
        start_date: today.toISOString().split('T')[0],
        end_date: nextWeek.toISOString().split('T')[0],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        working_hours_start: 9,
        working_hours_end: 17,
        token_data: {
          provider: calendarConnection.provider,
          access_token: calendarConnection.access_token,
          refresh_token: calendarConnection.refresh_token,
          token_uri: calendarConnection.token_uri,
          client_id: calendarConnection.client_id,
          client_secret: calendarConnection.client_secret,
          scopes: calendarConnection.scopes
        }
      };

      console.log('🔍 Suggested times request payload:', requestPayload);
      console.log(`🔍 ${calendarConnection.provider} connection object:`, calendarConnection);
      console.log('🔍 Token data fields:', Object.keys(calendarConnection));
      setDebugInfo(`Making request to ${BACKEND_URL}/calendar/suggest-times with ${calendarConnection.provider} calendar and token fields: ${Object.keys(calendarConnection).join(', ')}`);

      const response = await fetch(`${BACKEND_URL}/calendar/suggest-times`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('🔍 Backend response status:', response.status);
      const data = await response.json();
      console.log('🔍 Backend response data:', data);

      if (data.success) {
        setSuggestedTimes(data.suggestions.slice(0, 6));
        setDebugInfo(`✅ Found ${data.suggestions.length} available time slots`);
      } else {
        let errorMessage = data.error;
        if (data.error && data.error.includes('Failed to reconstruct credentials')) {
          errorMessage = 'Calendar connection outdated. Please reconnect your calendar in Profile settings.';
        }
        setDebugInfo(`❌ Backend error: ${errorMessage}`);
        console.error('Backend error:', data.error);
      }

    } catch (error) {
      console.error('Error loading suggested times:', error);
      setDebugInfo(`❌ Request failed: ${error.message}`);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const selectSuggestedTime = (suggestion, index) => {
    const startTime = new Date(suggestion.start_time);
    const localDate = startTime.toISOString().split('T')[0];
    const localTime = startTime.toTimeString().slice(0, 5);

    setInterviewDate(localDate);
    setInterviewTime(localTime);
    setSelectedTimeSlot(index);
    updateStatus(t('interviewScheduler.status.slotSelected'), 'success');
  };

  const scheduleInterview = async () => {
    const messageLanguage = getUiLocale();
    if (!applicantData) {
      updateStatus(t('interviewScheduler.status.missingApplicant'), 'error');
      return;
    }

    if (!interviewDate || !interviewTime) {
      updateStatus(t('interviewScheduler.status.missingDate'), 'error');
      return;
    }

    if (calendarConnections.length === 0) {
      updateStatus(t('interviewScheduler.status.noCalendar'), 'error');
      return;
    }

    setIsScheduling(true);
    const isRescheduling = existingInterview !== null;
    updateStatus(isRescheduling ? t('interviewScheduler.status.updatingEvent') : t('interviewScheduler.status.creatingEvent'));

    try {
      // Find any calendar connection (Google or Outlook)
      const calendarConnection = calendarConnections.find(conn =>
        conn.provider === 'google' || conn.provider === 'outlook'
      );

      if (!calendarConnection) {
        updateStatus(t('interviewScheduler.status.unsupportedCalendar'), 'error');
        setIsScheduling(false);
        return;
      }

      const startDateTime = new Date(`${interviewDate}T${interviewTime}`);
      const endDateTime = new Date(startDateTime.getTime() + (duration * 60000));

      // Get selected collaborators' emails and names
      const selectedCollaboratorEmails = selectedCollaborators.map(collabId => {
        const member = teamMembers.find(m => m.uid === collabId);
        return member ? member.email : null;
      }).filter(email => email !== null);

      const selectedCollaboratorNames = selectedCollaborators.map(collabId => {
        const member = teamMembers.find(m => m.uid === collabId);
        return member ? member.name : null;
      }).filter(name => name !== null);

      // Deduplicate emails and names in case the same person appears multiple times
      const uniqueCollaboratorEmails = [...new Set(selectedCollaboratorEmails)];
      const uniqueCollaboratorNames = [...new Set(selectedCollaboratorNames)];

      // Use interviewer names from substage data
      const interviewerNamesStr = uniqueCollaboratorNames.length > 0
        ? uniqueCollaboratorNames.join(', ')
        : interviewerName || 'Interview Team';

      // Include applicant and selected collaborators in attendees list
      const attendeeEmails = [applicantData.email, ...uniqueCollaboratorEmails];

      let scheduleRequest, endpoint;

      if (isRescheduling) {
        // Use reschedule endpoint for existing interviews
        scheduleRequest = {
          existing_interview_id: existingInterview.id,
          existing_calendar_event_id: existingInterview.meetingId,
          applicant_id: applicantId,
          user_id: userUID,
          interview_title: interviewTitle,
          duration_minutes: duration,
          applicant_email: applicantData.email,
          applicant_name: applicantData.name,
          company_name: actualCompanyName || 'Our Company',
          job_title: jobTitle || 'Position',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          attendee_emails: attendeeEmails, // Include all attendees
          location_type: locationType,
          address: address,
          token_data: {
            provider: calendarConnection.provider,
            access_token: calendarConnection.access_token,
            refresh_token: calendarConnection.refresh_token,
            token_uri: calendarConnection.token_uri,
            client_id: calendarConnection.client_id,
            client_secret: calendarConnection.client_secret,
            scopes: calendarConnection.scopes
          }
        };
        endpoint = `${BACKEND_URL}/reschedule-interview`;
      } else {
        // Use schedule endpoint for new interviews
        scheduleRequest = {
          applicant_id: applicantId,
          user_id: userUID,
          interview_title: interviewTitle,
          duration_minutes: duration,
          applicant_email: applicantData.email,
          applicant_name: applicantData.name,
          company_name: actualCompanyName || 'Our Company',
          job_title: jobTitle || 'Position',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          attendee_emails: attendeeEmails, // Include all attendees
          location_type: locationType,
          address: address,
          token_data: {
            provider: calendarConnection.provider,
            access_token: calendarConnection.access_token,
            refresh_token: calendarConnection.refresh_token,
            token_uri: calendarConnection.token_uri,
            client_id: calendarConnection.client_id,
            client_secret: calendarConnection.client_secret,
            scopes: calendarConnection.scopes
          }
        };
        endpoint = `${BACKEND_URL}/schedule-interview`;
      }

      // Schedule or reschedule the interview
      const scheduleResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(scheduleRequest)
      });

      const scheduleData = await scheduleResponse.json();

      if (!scheduleData.success) {
        throw new Error(scheduleData.error || `Failed to ${isRescheduling ? 'reschedule' : 'schedule'} interview`);
      }

      updateStatus(t('interviewScheduler.status.sendingEmail'));

      // Send email notification
      const emailResponse = await fetch(`${BACKEND_URL}/send-interview-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyUID: userUID,
          language: messageLanguage,
          duration,
          rescheduling: isRescheduling,
          location_type: locationType,
          address,
          applicant_email: applicantData.email,
          applicant_name: applicantData.name,
          company_name: actualCompanyName || 'Our Company',
          job_title: jobTitle || 'Position',
          meeting_link: scheduleData.meeting_link,
          interview_date: formatUiDate(interviewDate, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }, messageLanguage),
          interview_time: interviewTime,
          interviewer_name: interviewerNamesStr
        })
      });

      const emailData = await emailResponse.json();

      const icsStartDateTime = new Date(`${interviewDate}T${interviewTime}`);
      const icsEndDateTime = new Date(icsStartDateTime.getTime() + (duration * 60000));

      if (!emailData.success) {
        console.warn('Email sending failed:', emailData.error);

        // Show selected collaborators in error message too
        const collaboratorNames = selectedCollaborators.map(collabId => {
          const member = teamMembers.find(m => m.uid === collabId);
          return member ? member.name : 'Unknown';
        });

        // Deduplicate names
        const uniqueNames = [...new Set(collaboratorNames)];

        const collaboratorText = uniqueNames.length > 0 ? uniqueNames.join(', ') : '';

        updateStatus(
          <div>
            {t('interviewScheduler.status.emailFailed', { action: isRescheduling ? t('interviewScheduler.status.rescheduled') : t('interviewScheduler.status.scheduled') })}
            {collaboratorText && (
              <>
                <br />
                {t('interviewScheduler.status.additionalInterviewers', { names: collaboratorText })}
              </>
            )}
            <br />
            <button
              onClick={() => downloadICSFile(
                interviewTitle,
                icsStartDateTime.toISOString(),
                icsEndDateTime.toISOString(),
                scheduleData.meeting_link,
                t('interviewScheduler.status.icsDescription', { job: jobTitle || t('interviewScheduler.position'), company: companyName || t('interviewScheduler.company') }),
                `${applicantData?.name?.replace(' ', '-') || 'interview'}.ics`
              )}
              style={{ '--light-control-fill': '#568ded',
                marginTop: '8px',
                padding: '6px 12px',
                backgroundColor: '#568ded',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              data-light-control={"filled"}>
              {t('interviewScheduler.status.downloadIcs')}
            </button>
          </div>,
          'warning'
        );
      } else {
        // Show selected collaborators in success message
        const collaboratorNames = selectedCollaborators.map(collabId => {
          const member = teamMembers.find(m => m.uid === collabId);
          return member ? member.name : 'Unknown';
        });

        // Deduplicate names
        const uniqueNames = [...new Set(collaboratorNames)];

        const collaboratorText = uniqueNames.length > 0
          ? uniqueNames.join(', ')
          : '';

        updateStatus(
          <div>
            {t('interviewScheduler.status.success', { action: isRescheduling ? t('interviewScheduler.status.rescheduled') : t('interviewScheduler.status.scheduled') })}
            <br />
            {t('interviewScheduler.status.inviteSent')}
            {collaboratorText && (
              <>
                <br />
                {t('interviewScheduler.status.additionalInterviewers', { names: collaboratorText })}
              </>
            )}
            <br />
            <button
              onClick={() => downloadICSFile(
                interviewTitle,
                icsStartDateTime.toISOString(),
                icsEndDateTime.toISOString(),
                scheduleData.meeting_link,
                t('interviewScheduler.status.icsDescription', { job: jobTitle || t('interviewScheduler.position'), company: companyName || t('interviewScheduler.company') }),
                `${applicantData?.name?.replace(' ', '-') || 'interview'}.ics`
              )}
              style={{ '--light-control-fill': '#568ded',
                marginTop: '8px',
                padding: '6px 12px',
                backgroundColor: '#568ded',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              data-light-control={"filled"}>
              {t('interviewScheduler.status.downloadBackup')}
            </button>
          </div>,
          'success'
        );
      }

      // Send chat notification
      updateStatus(t('interviewScheduler.status.sendingChat'));
      try {
        // Include collaborator information in chat message
        const collaboratorNames = selectedCollaborators.map(collabId => {
          const member = teamMembers.find(m => m.uid === collabId);
          return member ? member.name : 'Unknown';
        });

        // Deduplicate names
        const uniqueNames = [...new Set(collaboratorNames)];

        const chatMessage = buildInterviewMessage({ rescheduling: isRescheduling, jobTitle, companyName: actualCompanyName, date: interviewDate, time: interviewTime, interviewer: interviewerNamesStr, collaborators: uniqueNames, duration, locationType, address, meetingLink: scheduleData.meeting_link }, messageLanguage);

        // Use the correct connection key pattern for chat message
        let chatConnectionKey = `${userUID}_${applicantId}`;
        if (applicantId.startsWith('temp_')) {
          chatConnectionKey = `${userUID}_imported_${applicantId}`;
        }

        const messagesRef = ref(database, `connections/${chatConnectionKey}/messages`);
        const newMessageRef = push(messagesRef);
        await set(newMessageRef, {
          message: chatMessage,
          user: userUID, // Company/employer ID
          timestamp: new Date().toISOString(),
          type: isRescheduling ? 'interview_rescheduled' : 'interview_scheduled'
        });

        console.log('Chat notification sent successfully');
      } catch (chatError) {
        console.error('Chat notification failed:', chatError);
        // Don't fail the entire process if chat fails
      }

      // Save to Firebase
      const interviewDateTime = new Date(`${interviewDate}T${interviewTime}`);
      const reminderTime = new Date(interviewDateTime.getTime() - (60 * 60 * 1000)); // 1 hour before

      const interviewData = {
        applicantId: applicantId,
        applicantName: applicantData.name,
        applicantEmail: applicantData.email,
        companyId: userUID,
        companyName: actualCompanyName || 'Our Company',
        jobTitle: jobTitle || 'Position',
        title: interviewTitle,
        date: interviewDate,
        time: interviewTime,
        dateTime: interviewDateTime.toISOString(),
        duration: duration,
        interviewer: interviewerNamesStr,
        locationType: locationType,
        address: address,
        meetingLink: scheduleData.meeting_link,
        meetingId: scheduleData.meeting_id,
        notes: additionalNotes,
        status: 'scheduled',
        createdAt: isRescheduling ? (existingInterview.createdAt || new Date().toISOString()) : new Date().toISOString(),
        emailSent: emailData.success,
        chatSent: true,
        reminderTime: reminderTime.toISOString(),
        reminderSent: false,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // Store selected collaborators
        collaborators: selectedCollaborators.map(collabId => {
          const member = teamMembers.find(m => m.uid === collabId);
          return member ? {
            uid: member.uid,
            name: member.name,
            email: member.email,
            isOwner: member.isOwner
          } : null;
        }).filter(collab => collab !== null),
        attendeeEmails: attendeeEmails // Store all attendee emails
      };

      // Persist the workflow association so multiple department-specific
      // interview rounds can display only their own schedule and analysis.
      const workflowSubstageId = substageData?.id || existingInterview?.workflowSubstageId;
      const workflowStageId = substageData?.stageId || existingInterview?.workflowStageId;
      if (workflowSubstageId) interviewData.workflowSubstageId = workflowSubstageId;
      if (workflowStageId) interviewData.workflowStageId = workflowStageId;

      // Only add rescheduledAt field when actually rescheduling
      if (isRescheduling) {
        interviewData.rescheduledAt = new Date().toISOString();
      }

      // For rescheduling, update the existing interview record
      // For new interviews, create a new record
      const interviewId = isRescheduling ? existingInterview.id : scheduleData.meeting_id;
      const applicantInterviewRef = ref(database, `companies/${userUID}/interviews/${applicantId}/${interviewId}`);
      await set(applicantInterviewRef, interviewData);

      // Update connection record (use the same connection key pattern)
      let updateConnectionKey = `${userUID}_${applicantId}`;
      if (applicantId.startsWith('temp_')) {
        updateConnectionKey = `${userUID}_imported_${applicantId}`;
      }

      const connectionUpdateRef = ref(database, `connections/${updateConnectionKey}`);
      const connectionUpdate = {
        meetingLink: scheduleData.meeting_link
      };

      // Only add defined fields to avoid Firebase undefined errors
      if (isRescheduling) {
        connectionUpdate.lastInterviewRescheduled = new Date().toISOString();
      } else {
        connectionUpdate.lastInterviewScheduled = new Date().toISOString();
      }

      await update(connectionUpdateRef, connectionUpdate);

      emitAtsAnalyticsEvent({
        company_uid: userUID,
        event_type: isRescheduling ? 'interview_rescheduled' : 'interview_scheduled',
        occurred_at: isRescheduling ? interviewData.rescheduledAt || interviewData.createdAt : interviewData.createdAt,
        subject: {
          applicantId,
          candidateId: applicantData?.candidateId || applicantId,
          applicantType: applicantId?.startsWith('temp_') ? 'imported' : 'authenticated'
        },
        job: {
          jobId: applicantData?.roleId || '',
          jobTitle: interviewData.jobTitle || jobTitle || 'Position'
        },
        context: {
          interviewId,
          dateTime: interviewData.dateTime,
          durationMinutes: Number(interviewData.duration) || 0,
          status: interviewData.status || 'scheduled',
          connectionKey: applicantData?.connectionKey || '',
          ...buildActorAnalyticsContext({
            actorUserId: userUID,
            ownerRecruiterId: applicantData?.ownerRecruiterId || '',
            ownerRecruiterEmail: applicantData?.ownerRecruiterEmail || '',
            ...normalizeAnalyticsSource({
              utmSource: applicantData?.utmSource || '',
              source: applicantData?.primarySource || '',
              sourceCategory: applicantData?.sourceCategory || '',
              sourceTags: applicantData?.sourceTags || []
            })
          })
        }
      });

      if (onScheduleSuccess) {
        onScheduleSuccess({
          meetingLink: scheduleData.meeting_link,
          meetingId: scheduleData.meeting_id,
          interviewData: interviewData
        });
      }

      // Auto-close after 3 seconds
      setTimeout(() => {
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Interview scheduling error:', error);
      updateStatus(t('interviewScheduler.status.error', {
        action: isRescheduling ? t('interviewScheduler.status.rescheduling') : t('interviewScheduler.status.scheduling'),
        message: error.message
      }), 'error');
    } finally {
      setIsScheduling(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Generate ICS calendar file for download
  const generateICSFile = (title, startTime, endTime, location, description) => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    // Format dates for ICS (UTC format)
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//JOBSEEQR//Interview Scheduler//EN
CALSCALE:GREGORIAN
BEGIN:VEVENT
UID:${Date.now()}@jobseeqr.co
DTSTART:${formatICSDate(start)}
DTEND:${formatICSDate(end)}
SUMMARY:${title}
DESCRIPTION:${description}\\n\\nMeeting Link: ${location}
LOCATION:${location}
STATUS:CONFIRMED
BEGIN:VALARM
TRIGGER:-PT1H
DESCRIPTION:Interview Reminder - 1 Hour
ACTION:DISPLAY
END:VALARM
BEGIN:VALARM
TRIGGER:-PT15M
DESCRIPTION:Interview Reminder - 15 Minutes
ACTION:DISPLAY
END:VALARM
END:VEVENT
END:VCALENDAR`;

    return icsContent;
  };

  // Download ICS file
  const downloadICSFile = (title, startTime, endTime, location, description, fileName) => {
    const icsContent = generateICSFile(title, startTime, endTime, location, description);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName || 'interview.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="modal-overlay" data-tutorial="interview-scheduler-modal" onClick={onClose}>
      <div
        className="modal-content"
        data-tutorial="interview-scheduler-content"
        onClick={(e) => e.stopPropagation()}
        style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}
      >
        {/* Header */}
        <div className="modal-header">
          <h2 style={{ fontSize: '20px', fontWeight: '600' }}>
            <i className="fa-solid fa-calendar-plus" style={{ marginRight: '10px', color: '#568ded' }}></i>
            {existingInterview
              ? t('interviewScheduler.headerReschedule', { title: substageTitle || t('interview.title'), name: applicantData?.name || t('interviewScheduler.candidate') })
              : t('interviewScheduler.headerSchedule', { title: substageTitle || t('interview.title'), name: applicantData?.name || t('interviewScheduler.candidate') })
            }
          </h2>
          <div className="close" onClick={onClose}>&times;</div>
        </div>

        <div className="modal-page-container">
          <div className="form-section">
            {/* Calendar Connection Status */}
            {calendarConnections.length === 0 && (
              <div style={{
                backgroundColor: '#F8D7DA',
                padding: '16px 20px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid #F5C6CB'
              }}>
                <p style={{ margin: 0, color: 'var(--app-tone-721c24, #721C24)', fontWeight: '600' }}>
                  <i className="fa-solid fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                  {t('interviewScheduler.noCalendar')}
                </p>
              </div>
            )}

            {/* Location Info */}
            <div style={{ marginBottom: '24px', padding: '12px 16px', backgroundColor: '#F0F4FF', borderRadius: '8px', border: '1px solid #D6E4FF' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--app-heading, #031128)' }}>
                <i className={`fa-solid ${locationType === 'in-person' ? 'fa-map-marker-alt' : 'fa-video'}`} style={{ color: '#568ded' }}></i>
                <span style={{ fontWeight: '600' }}>{t('interviewScheduler.locationLabel')}</span>
                <span>
                  {locationType === 'in-person'
                    ? (address ? t('interviewScheduler.locationInPersonAt', { address }) : t('interviewScheduler.locationInPerson'))
                    : t('interviewScheduler.locationVirtual')}
                </span>
              </div>
            </div>

            {/* Quick Schedule Section */}
            {calendarConnections.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  <i className="fa-solid fa-clock" style={{ marginRight: '8px', color: '#568ded' }}></i>
                  {t('interviewScheduler.quickSchedule')}
                </h3>

                {isLoadingSuggestions ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '30px 20px',
                    backgroundColor: '#F8F9FA',
                    borderRadius: '12px',
                    border: '1px solid #E0E0E0'
                  }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '20px', color: '#568ded', marginBottom: '8px' }}></i>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{t('interviewScheduler.findingTimes')}</p>
                  </div>
                ) : suggestedTimes.length > 0 ? (
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    overflowX: 'auto',
                    paddingBottom: '8px',
                    marginBottom: '16px'
                  }}>
                    {suggestedTimes.map((suggestion, index) => {
                      const isSelected = selectedTimeSlot === index;
                      return (
                        <button
                          key={index}
                          onClick={() => selectSuggestedTime(suggestion, index)}
                          style={{ '--light-control-fill': isSelected ? '#568ded' : '#FFFFFF',
                            padding: '16px',
                            backgroundColor: isSelected ? '#568ded' : '#FFFFFF',
                            border: isSelected ? '2px solid #031128' : '2px solid #568ded',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '13px',
                            fontFamily: 'Montserrat',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            color: isSelected ? 'var(--app-text, #FFFFFF)' : 'var(--app-heading, #031128)',
                            minWidth: '240px',
                            maxWidth: '240px',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                            transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                            boxShadow: isSelected ? '0 6px 16px rgba(86, 141, 237, 0.3)' : 'none'
                          }}
                          onMouseOver={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#F8F9FA';
                              e.currentTarget.style.borderColor = '#031128';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#FFFFFF';
                              e.currentTarget.style.borderColor = '#568ded';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                          data-light-control={isSelected ? "filled" : "quiet"}>
                          {/* Selected indicator */}
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              backgroundColor: '#FFFFFF',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <i className="fa-solid fa-check" style={{ color: '#568ded', fontSize: '12px' }}></i>
                            </div>
                          )}

                          <div style={{
                            fontWeight: '600',
                            fontSize: '16px',
                            marginBottom: '8px',
                            color: isSelected ? 'var(--app-text, #FFFFFF)' : 'var(--app-heading, #031128)'
                          }}>
                            {suggestion.day_name && suggestion.date_str
                              ? `${suggestion.day_name}, ${suggestion.date_str}`
                              : suggestion.start_time_display.split(' at ')[0]
                            }
                          </div>
                          <div style={{
                            fontSize: '15px',
                            marginBottom: '8px',
                            color: isSelected ? 'var(--app-text, #FFFFFF)' : '#568ded',
                            fontWeight: '500'
                          }}>
                            {suggestion.time_str || suggestion.start_time_display.split(' at ')[1] || suggestion.start_time_display} - {suggestion.end_time_display}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: isSelected ? 'var(--app-text, #FFFFFF)' : '#666',
                            fontWeight: '500',
                            backgroundColor: isSelected ? 'var(--app-surface, rgba(255,255,255,0.2))' : '#F8F9FA',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            display: 'inline-block'
                          }}>
                            {t('interviewScheduler.durationChip', { minutes: suggestion.duration_minutes })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#F8F9FA',
                    borderRadius: '12px',
                    border: '1px solid #E0E0E0'
                  }}>
                    <i className="fa-solid fa-calendar-xmark" style={{ fontSize: '24px', color: '#666', marginBottom: '12px' }}></i>
                    <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>{t('interviewScheduler.noTimes')}</p>
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={loadSuggestedTimes}
                        style={{ '--light-control-fill': '#568ded',
                          padding: '8px 16px',
                          backgroundColor: '#568ded',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: 'Montserrat'
                        }}
                        data-light-control={"filled"}>
                        <i className="fa-solid fa-refresh" style={{ marginRight: '6px' }}></i>
                        {t('interviewScheduler.tryAgain')}
                      </button>
                      <button
                        onClick={() => window.location.href = '/external-apps#calendar'}
                        style={{ '--light-control-fill': '#28a745',
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: 'Montserrat'
                        }}
                        data-light-control={"filled"}>
                        <i className="fa-solid fa-link" style={{ marginRight: '6px' }}></i>
                        {t('interviewScheduler.reconnect')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Manual Scheduling Form */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                <i className="fa-solid fa-edit" style={{ marginRight: '8px', color: '#568ded' }}></i>
                {t('interviewScheduler.manualHeading')}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="input-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--app-heading, #031128)' }}>
                    {t('interviewScheduler.dateLabel')}
                  </label>
                  <input
                    type="date"
                    value={interviewDate}
                    onChange={(e) => setInterviewDate(e.target.value)}
                    min={getMinDate()}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontFamily: 'Montserrat',
                      color: 'var(--app-heading, #031128)',
                      backgroundColor: '#FFFFFF',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div className="input-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--app-heading, #031128)' }}>
                    {t('interviewScheduler.timeLabel')}
                  </label>
                  <input
                    type="time"
                    value={interviewTime}
                    onChange={(e) => setInterviewTime(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #E0E0E0',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontFamily: 'Montserrat',
                      color: 'var(--app-heading, #031128)',
                      backgroundColor: '#FFFFFF',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Status Message */}
            {scheduleStatus && (
              <div style={{
                padding: '16px 20px',
                borderRadius: '12px',
                marginBottom: '24px',
                backgroundColor: statusTone === 'error' ? '#F8D7DA' :
                  statusTone === 'success' ? '#D4EDDA' : '#FFF3CD',
                color: statusTone === 'error' ? 'var(--app-tone-721c24, #721C24)' :
                  statusTone === 'success' ? 'var(--app-tone-155724, #155724)' : 'var(--app-tone-856404, #856404)',
                border: `1px solid ${statusTone === 'error' ? '#F5C6CB' :
                  statusTone === 'success' ? '#C3E6CB' : '#FFEAA7'}`,
                fontSize: '16px',
                fontFamily: 'Montserrat',
                fontWeight: '600'
              }}>
                {scheduleStatus}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            {t('interviewScheduler.cancel')}
          </button>

          <button
            className="btn-primary"
            onClick={scheduleInterview}
            disabled={isScheduling || calendarConnections.length === 0}
            style={{
              opacity: isScheduling || calendarConnections.length === 0 ? 0.6 : 1,
              cursor: isScheduling || calendarConnections.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {isScheduling ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                {existingInterview ? t('interviewScheduler.rescheduling') : t('interviewScheduler.scheduling')}
              </>
            ) : (
              <>
                <i className="fa-solid fa-calendar-check" style={{ marginRight: '8px' }}></i>
                {existingInterview ? t('interviewScheduler.reschedule') : t('interviewScheduler.schedule')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewScheduler; 
